import { inspectionsService } from '@/services/inspections';
import { useRequirePermissions } from '@/shared/hooks';
import { toaster } from '@/shared/ui/toaster.component';
import { formatCurrency, formatDateToBrazil } from '@/shared/utils';
import { formatPercent } from '@/shared/utils/format-percent.util';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { resolveCapitalGlobalDocumentLabel } from '@/features/quotes/utils/resolve-capital-global-id-type.util';
import { formatZipcode } from '@/shared/utils/format-zipcode.util';
import { Button, Text } from '@noorden-insurtech/alba-design-system';
import {
  ChevronDown,
  ChevronUp,
  Eye,
  FilePlus,
  FileText,
  Folder,
  History,
  MessageSquareText,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import type {
  PolicyDetailsAgent,
  PolicyDetailsResponse,
} from '../../../services/policies/types';
import {
  AccordionSection,
  getInsuranceTypeLabel,
  InfoRow,
  MobileLabeledField,
} from './policy-section.shared';
import {
  ImprovementLetterModal,
  InspectionAttachmentsModal,
  InspectionHistoryModal,
  InspectionInteractionsModal,
} from '../../inspections/components';
import { useImprovementLetter } from '../../inspections/hooks/use-improvement-letter.hook';
import { useInspectionHistory } from '../../inspections/hooks/use-inspection-history.hook';
import { usePolicyInspections } from '../../inspections/hooks/use-policy-inspections.hook';
import { useRecordsInspection } from '../../inspections/hooks/use-records-inspection.hook';
import {
  usePolicyProposalDocument,
  usePolicyQuoteDocument,
  useResolvePolicyProposalDocument,
  useResolvePolicyQuoteDocument,
} from '../hooks/use-policy-documents.hook';
import { CoverageCard } from '../../quotes/components/quote-details.component';
import { type BusinessLocation } from '../../quotes/components/quote-steps/business-location';
import { BusinessLocationViewCard } from './business-location-view-card.component';
import { useConstructionTypes } from '../../quotes/hooks/homeowner/use-construction-types.hook';
import { useInsuredObjects } from '../../quotes/hooks/homeowner/use-insured-objects.hook';
import { usePropertyClassifications } from '../../quotes/hooks/homeowner/use-property-classifications.hook';
import { usePropertyTypes } from '../../quotes/hooks/homeowner/use-property-types.hook';
import { parseProductCode } from '../../quotes/utils/parse-product-code';
import type { BrokerWriteGuard } from '../../brokers/utils/broker-status.util';

type PolicyTabProps = {
  data: PolicyDetailsResponse;
  onDownload?: (
    title: string,
    message: string,
    url: string,
    filename: string
  ) => void;
  /** Corretora inativa: PDFs de cotação/proposta travados (Api barra com 403). */
  downloadGuard?: BrokerWriteGuard;
};

export function PolicyTab({ data, onDownload, downloadGuard }: PolicyTabProps) {
  const { data: propertyTypes } = usePropertyTypes();
  const { data: constructionTypes } = useConstructionTypes();
  const { data: propertyClassifications } = usePropertyClassifications();
  const { data: insuredObjectTypes } = useInsuredObjects();
  const productCode = data.productCode ?? '';
  const { data: quoteDocument, isLoading: isLoadingQuoteDocument } =
    usePolicyQuoteDocument(data.policyId, productCode);
  const { data: proposalDocument, isLoading: isLoadingProposalDocument } =
    usePolicyProposalDocument(data.policyId, productCode);
  const resolveQuoteDocument = useResolvePolicyQuoteDocument();
  const resolveProposalDocument = useResolvePolicyProposalDocument();
  const [expandedLocationIds, setExpandedLocationIds] = useState<Set<string>>(
    new Set()
  );

  // O empresarial (180007) não persiste os PDFs de cotação/proposta no fluxo de
  // emissão, então o anexo pode não existir: aqui o PDF é gerado sob demanda e
  // fica salvo para os próximos acessos.
  const handleDownloadDocument = useCallback(
    async (
      kind: 'quote' | 'proposal',
      cached: { key: string; url: string } | null | undefined
    ) => {
      if (!onDownload || !data.policyId) return;

      const isQuote = kind === 'quote';
      const title = isQuote ? 'PDF Cotação' : 'PDF Proposta';
      const message = isQuote
        ? 'O PDF da cotação será baixado para o seu dispositivo.'
        : 'O PDF da proposta será baixado para o seu dispositivo.';
      const fallbackFilename = isQuote
        ? `cotacao-${data.policyId}.pdf`
        : `proposta-${data.policyId}.pdf`;

      const resolver = isQuote ? resolveQuoteDocument : resolveProposalDocument;

      try {
        const document =
          cached ??
          (await resolver.mutateAsync({
            policyId: data.policyId,
            productCode,
          }));

        onDownload(
          title,
          message,
          document.url,
          document.key.split('/').pop() || fallbackFilename
        );
      } catch (error) {
        toaster.create({
          type: 'error',
          title: `Erro ao baixar ${title}`,
          description:
            error instanceof Error
              ? error.message
              : 'Não foi possível obter o PDF. Tente novamente.',
        });
      }
    },
    [
      data.policyId,
      onDownload,
      productCode,
      resolveProposalDocument,
      resolveQuoteDocument,
    ]
  );

  const customer = data.customer;
  const risk = data.lobs?.[0]?.risks?.[0];
  const coverages = risk?.coverages ?? [];
  const agents = data.agents ?? [];
  const mainBroker = agents[0];
  const coBroker = agents.find((a, i) => i > 0 && (a.shareRate ?? 0) > 0);
  // O `commissionRate` do agente vem da emissão já rateado pela participação
  // (`commission = corretagem x shareRate`, ver StepCommercialization), então a
  // corretagem "pura" é a razão entre os dois — mesma conta do resumo da
  // proposta e do edit-quote-form. `shareRate` sozinho é PARTICIPAÇÃO, não
  // corretagem: usá-lo aqui fazia a página exibir sempre 100%.
  const brokerageRate = (agent?: PolicyDetailsAgent) => {
    if (!agent || agent.commissionRate == null) return '-';
    const shareRate = agent.shareRate ?? 0;
    return formatPercent(
      shareRate > 0 ? agent.commissionRate / shareRate : agent.commissionRate
    );
  };

  const mainBrokerCommission = brokerageRate(mainBroker);
  const coBrokerCommission = brokerageRate(coBroker);
  const mainBrokerParticipation =
    mainBroker?.shareRate != null ? formatPercent(mainBroker.shareRate) : '-';
  const coBrokerParticipation =
    coBroker?.shareRate != null ? formatPercent(coBroker.shareRate) : '-';

  const nonAssistCoverages = coverages.filter(
    c => c.productElementCode !== 'ASSIST'
  );
  const assistanceCoverage = coverages.find(
    c => c.productElementCode === 'ASSIST'
  );

  const propertyTypeLabel =
    propertyTypes?.find((p: any) => String(p.code) === risk?.propertyType)
      ?.name ??
    risk?.propertyType ??
    '-';
  const constructionTypeLabel =
    constructionTypes?.find(
      (c: any) => String(c.code) === risk?.constructionType
    )?.description ??
    risk?.constructionType ??
    '-';
  const classificationLabel =
    propertyClassifications?.find(
      (c: any) => String(c.code) === risk?.propertyClassification
    )?.description ??
    risk?.propertyClassification ??
    '-';

  const scheduleExtItems = risk?.scheduleExt ?? [];
  const isBusinessQuote =
    parseProductCode(data.productCode ?? '') === 'BUSINESS_INSURANCE';

  const businessLocationCards = useMemo<BusinessLocation[]>(() => {
    if (!isBusinessQuote || !data) return [];
    // Aceita 'ASSIST' ou códigos terminados em 'ASS' (ex.: 'EMPASS') —
    // mesma regra do util de quote (`map-business-risks-to-locations`).
    const isAssistanceCode = (code?: string) => {
      const upper = (code || '').trim().toUpperCase();
      return upper === 'ASSIST' || upper.endsWith('ASS');
    };
    return (data.lobs?.[0]?.risks ?? []).map((risk, index) => {
      const assistanceCov = (risk.coverages ?? []).find(c =>
        isAssistanceCode(c.productElementCode)
      );
      const packageCode =
        assistanceCov?.planCode || assistanceCov?.productElementCode || '';
      const firstScheduleItem = risk.scheduleExt?.[0];
      return {
        id: String(index),
        cep: formatZipcode(risk.zipCode || ''),
        street: risk.address || '',
        number: risk.addressNumber || '',
        complement: '',
        neighborhood: risk.neighborhood || '',
        city: risk.city || '',
        state: risk.state || '',
        activityType: risk.activityTypePerformed || '',
        activityTypeLabel: risk.activityTypePerformed || '',
        constructionType: risk.constructionType || '',
        constructionTypeLabel: risk.constructionType || '',
        isLandmarked:
          risk.listedPropertyFlag === 'Y' || risk.listedPropertyFlag === 'S',
        insuredObjectType: firstScheduleItem?.insuredObject ?? '',
        insuredObjectTypeLabel: firstScheduleItem?.insuredObject ?? '',
        riskValue:
          firstScheduleItem?.value != null
            ? formatCurrency(firstScheduleItem.value)
            : '',
        assistancePackage: assistanceCov?.productElementDesc || packageCode,
        assistancePackageCode: packageCode,
        // Ordem fixa: [fire, upperFloor, insideMall, theft] — bate com
        // QUESTIONNAIRE_LABELS / QUESTION_KIND no BusinessLocationViewCard.
        questionnaireAnswers: [
          risk.fireProtectionSystems ?? [],
          risk.isUpperFloor ?? '',
          risk.isInsideMall ?? '',
          risk.securityAlarmSystems ?? [],
        ],
        propertyType: risk.propertyType || '',
        propertyClassification: risk.propertyClassification || '',
        coveragesPayload: (risk.coverages ?? [])
          .filter(c => !isAssistanceCode(c.productElementCode))
          .map(c => ({
            code: c.productElementCode ?? '',
            value: c.sumInsured ?? 0,
            displayName: c.productElementDesc,
          })),
        benefitsPayload: (assistanceCov?.benefits ?? []).map(b => ({
          code: b.productElementCode ?? '',
          value: b.sumInsured ?? 0,
          displayName: b.productElementDesc,
        })),
      };
    });
  }, [isBusinessQuote, data]);

  const allLocationsExpanded =
    businessLocationCards.length > 0 &&
    businessLocationCards.every(loc => expandedLocationIds.has(loc.id));

  const toggleLocation = (key: string) => {
    setExpandedLocationIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllLocations = () => {
    if (allLocationsExpanded) {
      setExpandedLocationIds(new Set());
    } else {
      setExpandedLocationIds(new Set(businessLocationCards.map(loc => loc.id)));
    }
  };

  return (
    <div className='flex flex-col gap-2 mt-6'>
      {/* Dados da Emissão */}
      <AccordionSection title='Dados da Emissão'>
        <div className='hidden sm:grid sm:grid-cols-4 sm:gap-6'>
          <InfoRow
            label='Início de Vigência'
            value={formatDateToBrazil(data.effectiveDate)}
          />
          <InfoRow
            label='Fim de Vigência'
            value={formatDateToBrazil(data.expiryDate)}
          />
          <InfoRow
            label='Data da Emissão'
            value={formatDateToBrazil(
              data.externalEffectiveDate || data.effectiveDate
            )}
          />
          <InfoRow
            label='Tipo de Seguro'
            value={getInsuranceTypeLabel(data.typeOfInsurance)}
          />
        </div>
        <div className='grid grid-cols-2 gap-x-4 gap-y-4 sm:hidden'>
          <MobileLabeledField
            label='Início de Vigência'
            value={formatDateToBrazil(data.effectiveDate)}
            showCalendar
            withDivider={false}
          />
          <MobileLabeledField
            label='Fim de Vigência'
            value={formatDateToBrazil(data.expiryDate)}
            showCalendar
            withDivider={false}
          />
          <MobileLabeledField
            label='Data da Emissão'
            value={formatDateToBrazil(data.externalEffectiveDate || data.effectiveDate)}
            showCalendar
            withDivider={false}
          />
          <div className='flex flex-col gap-1 min-w-0'>
            <span className='text-sm font-bold text-alba-black-500'>Tipo de Seguro</span>
            <div className='flex items-center gap-1 text-sm text-alba-gray-600'>
              {['2', '3'].includes(String(data.typeOfInsurance ?? '').trim()) ? (
                <FileText className='h-[18px] w-[18px] shrink-0 text-alba-gray-500' />
              ) : (
                <FilePlus className='h-[18px] w-[18px] shrink-0 text-alba-gray-500' />
              )}
              <span className='whitespace-nowrap'>{getInsuranceTypeLabel(data.typeOfInsurance)}</span>
            </div>
          </div>
        </div>
      </AccordionSection>

      {/* Dados do Segurado */}
      <AccordionSection title='Dados do Segurado'>
        <div className='hidden sm:grid sm:grid-cols-4 sm:gap-6'>
          <InfoRow
            label={resolveCapitalGlobalDocumentLabel(customer?.documentType)}
            value={
              formatRegisterNumber(
                customer?.cpf ?? customer?.documentNumber ?? ''
              ) ?? '-'
            }
          />
          <InfoRow label='Nome' value={customer?.name} />
          <InfoRow label='E-mail' value={customer?.email} />
          <InfoRow
            label='Telefone'
            value={customer?.phone || customer?.cellphone}
          />
        </div>
        <div className='flex flex-col gap-2 sm:hidden'>
          <MobileLabeledField
            label={resolveCapitalGlobalDocumentLabel(customer?.documentType)}
            value={customer?.cpf ? formatRegisterNumber(customer.cpf) : '-'}
          />
          <MobileLabeledField label='Nome' value={customer?.name} />
          <MobileLabeledField label='E-mail' value={customer?.email} />
          <MobileLabeledField
            label='Telefone'
            value={customer?.phone || customer?.cellphone}
            withDivider={false}
          />
        </div>
      </AccordionSection>

      {/* Dados do Imóvel — só fora do Empresarial. No Empresarial, o endereço
          fica dentro de cada Local de Risco. */}
      {!isBusinessQuote && (
        <AccordionSection title='Dados do Imóvel'>
          <div className='hidden sm:grid sm:grid-cols-2 sm:gap-6 lg:grid-cols-4'>
            <InfoRow
              label='CEP'
              value={risk?.zipCode ? formatZipcode(risk.zipCode) : '-'}
            />
            <InfoRow
              label='Endereço'
              value={
                risk?.address
                  ? `${risk.address}${risk.addressNumber ? `, ${risk.addressNumber}` : ''}`
                  : '-'
              }
            />
            <InfoRow label='Bairro' value={risk?.neighborhood} />
            <InfoRow label='Cidade' value={risk?.city} />
            <InfoRow label='Estado' value={risk?.state} />
          </div>
          <div className='flex flex-col gap-3 sm:hidden'>
            <div className='grid grid-cols-3 gap-3'>
              <div className='col-span-1 rounded-lg bg-alba-gray-100/40 px-3 py-2'>
                <InfoRow
                  label='CEP'
                  value={risk?.zipCode ? formatZipcode(risk.zipCode) : '-'}
                />
              </div>
              <div className='col-span-2 rounded-lg bg-alba-gray-100/40 px-3 py-2'>
                <InfoRow
                  label='Endereço'
                  value={
                    risk?.address
                      ? `${risk.address}${risk.addressNumber ? `, ${risk.addressNumber}` : ''}`
                      : '-'
                  }
                />
              </div>
            </div>
            <div className='grid grid-cols-3 gap-3'>
              <div className='rounded-lg bg-alba-gray-100/40 px-3 py-2'>
                <InfoRow label='Bairro' value={risk?.neighborhood} />
              </div>
              <div className='rounded-lg bg-alba-gray-100/40 px-3 py-2'>
                <InfoRow label='Cidade' value={risk?.city} />
              </div>
              <div className='rounded-lg bg-alba-gray-100/40 px-3 py-2'>
                <InfoRow label='Estado' value={risk?.state} />
              </div>
            </div>
          </div>
        </AccordionSection>
      )}

      {/* Dados do Risco */}
      <AccordionSection title='Dados do Risco'>
        {isBusinessQuote ? (
          <div className='flex flex-col items-start p-0 gap-4 self-stretch'>
            <div className='flex items-center justify-between w-full'>
              <Text className='text-base text-alba-black-500'>
                Lista de Locais
              </Text>
              <button
                onClick={toggleAllLocations}
                className='flex items-center gap-2 px-4 py-2 rounded-lg text-alba-blue-500 hover:bg-alba-gray-100 transition-colors'
              >
                <span className='text-xs font-medium'>
                  {allLocationsExpanded
                    ? 'Fechar Todos'
                    : `Abrir Todos (${businessLocationCards.length})`}
                </span>
                {allLocationsExpanded ? (
                  <ChevronUp className='h-4 w-4' strokeWidth={1.6} />
                ) : (
                  <ChevronDown className='h-4 w-4' strokeWidth={1.6} />
                )}
              </button>
            </div>
            <div className='flex flex-col gap-4 w-full'>
              {businessLocationCards.map((location, index) => (
                <BusinessLocationViewCard
                  key={location.id}
                  location={location}
                  locationNumber={index + 1}
                  isOpen={expandedLocationIds.has(location.id)}
                  onToggleLocation={() => toggleLocation(location.id)}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className='hidden sm:grid sm:grid-cols-2 sm:gap-6 lg:grid-cols-4'>
              <InfoRow label='Tipo do Imóvel' value={propertyTypeLabel} />
              <InfoRow
                label='Imóvel Tombado?'
                value={risk?.listedPropertyFlag === 'Y' ? 'Sim' : 'Não'}
              />
              <InfoRow
                label='Tipo de Construção'
                value={constructionTypeLabel}
              />
              <InfoRow
                label='Classificação do Imóvel'
                value={classificationLabel}
              />
              {scheduleExtItems.map((item, idx) => (
                <InfoRow
                  key={idx}
                  label='Objeto Segurado'
                  value={
                    insuredObjectTypes?.find(
                      o => String(o.code) === String(item.insuredObject)
                    )?.description ??
                    item.insuredObject ??
                    '-'
                  }
                />
              ))}
            </div>
            <div className='flex flex-col gap-2 sm:hidden'>
              <MobileLabeledField
                label='Tipo do Imóvel'
                value={propertyTypeLabel}
              />
              <MobileLabeledField
                label='Imóvel Tombado?'
                value={risk?.listedPropertyFlag === 'Y' ? 'Sim' : 'Não'}
              />
              <MobileLabeledField
                label='Tipo de Construção'
                value={constructionTypeLabel}
              />
              <MobileLabeledField
                label='Classificação do Imóvel'
                value={classificationLabel}
              />
              {scheduleExtItems.map((item, idx) => (
                <MobileLabeledField
                  key={idx}
                  label='Objeto Segurado'
                  value={
                    insuredObjectTypes?.find(
                      o => String(o.code) === String(item.insuredObject)
                    )?.description ??
                    item.insuredObject ??
                    '-'
                  }
                  withDivider={idx !== scheduleExtItems.length - 1}
                />
              ))}
            </div>
          </>
        )}
      </AccordionSection>

      {/* Coberturas — só fora do Empresarial. No Empresarial, as coberturas
          ficam dentro de cada Local de Risco. */}
      {!isBusinessQuote && (
        <AccordionSection title='Coberturas'>
          <div className='flex flex-col gap-4'>
            {/* Coberturas (não-assistência) */}
            {nonAssistCoverages.length > 0 && (
              <CoverageCard
                title='Básicas'
                defaultExpanded
                items={nonAssistCoverages.map(c => ({
                  name: c.productElementDesc || c.productElementCode || '-',
                  value: formatCurrency(c.sumInsured ?? 0),
                }))}
              />
            )}

            {/* Assistências */}
            {assistanceCoverage && (
              <CoverageCard
                title='Assistências'
                defaultExpanded
                totalValue={formatCurrency(
                  assistanceCoverage.grossPremium ??
                  assistanceCoverage.sumInsured ??
                  0
                )}
                items={
                  assistanceCoverage.benefits?.map(b => ({
                    name: b.productElementDesc || b.productElementCode || '-',
                    value: formatCurrency(b.sumInsured ?? 0),
                  })) ?? []
                }
              />
            )}
          </div>
        </AccordionSection>
      )}

      {/* Comercialização */}
      <AccordionSection title='Comercialização'>
        <div className='flex flex-col gap-4'>
          <span className='text-sm font-bold text-alba-black-500'>
            Dados do Corretor
          </span>
          <div className='hidden sm:grid sm:grid-cols-3 sm:gap-6'>
            <InfoRow label='Corretor' value={mainBroker?.name} />
            <InfoRow
              label='Percentual de Corretagem'
              value={mainBrokerCommission}
            />
            {coBroker && (
              <InfoRow label='Participação' value={mainBrokerParticipation} />
            )}
            <InfoRow label='Co-corretagem?' value={coBroker ? 'Sim' : 'Não'} />
          </div>
          <div className='flex flex-col gap-2 sm:hidden'>
            <MobileLabeledField label='Corretor' value={mainBroker?.name} />
            <MobileLabeledField
              label='Percentual de Corretagem'
              value={mainBrokerCommission}
            />
            {coBroker && (
              <MobileLabeledField
                label='Participação'
                value={mainBrokerParticipation}
              />
            )}
            <MobileLabeledField
              label='Co-corretagem?'
              value={coBroker ? 'Sim' : 'Não'}
              withDivider={!coBroker}
            />
          </div>
          {coBroker && (
            <>
              <div className='hidden sm:grid sm:grid-cols-3 sm:gap-6 sm:mt-2'>
                <InfoRow label='Co-Corretor' value={coBroker.name} />
                <InfoRow
                  label='CNPJ'
                  value={
                    coBroker.registerNumber
                      ? formatRegisterNumber(coBroker.registerNumber)
                      : '-'
                  }
                />
                <InfoRow
                  label='Percentual de Corretagem'
                  value={coBrokerCommission}
                />
                <InfoRow label='Participação' value={coBrokerParticipation} />
              </div>
              <div className='flex flex-col gap-2 sm:hidden'>
                <MobileLabeledField label='Co-Corretor' value={coBroker.name} />
                <MobileLabeledField
                  label='CNPJ'
                  value={
                    coBroker.registerNumber
                      ? formatRegisterNumber(coBroker.registerNumber)
                      : '-'
                  }
                />
                <MobileLabeledField
                  label='Percentual de Corretagem'
                  value={coBrokerCommission}
                />
                <MobileLabeledField
                  label='Participação'
                  value={coBrokerParticipation}
                  withDivider={false}
                />
              </div>
            </>
          )}
        </div>
      </AccordionSection>

      {/* Documentação */}
      <AccordionSection title='Documentação'>
        <div className='flex flex-col gap-3 sm:flex-row'>
          <Button
            variant='secondary'
            size='sm'
            className='w-full sm:w-auto'
            disabled={
              isLoadingQuoteDocument ||
              resolveQuoteDocument.isPending ||
              downloadGuard?.blocked
            }
            title={downloadGuard?.reason}
            onClick={() => handleDownloadDocument('quote', quoteDocument)}
          >
            <Eye className='w-4 h-4 mr-2' />
            {resolveQuoteDocument.isPending ? 'Gerando...' : 'PDF Cotação'}
          </Button>
          <Button
            variant='secondary'
            size='sm'
            className='w-full sm:w-auto'
            disabled={
              isLoadingProposalDocument ||
              resolveProposalDocument.isPending ||
              downloadGuard?.blocked
            }
            title={downloadGuard?.reason}
            onClick={() => handleDownloadDocument('proposal', proposalDocument)}
          >
            <Eye className='w-4 h-4 mr-2' />
            {resolveProposalDocument.isPending ? 'Gerando...' : 'PDF Proposta'}
          </Button>
        </div>
      </AccordionSection>

      {/* Inspeção */}
      {data.policyId && <InspectionSection policyId={data.policyId} />}
    </div>
  );
}

function InspectionSection({ policyId }: { policyId: string }) {
  // Só subscritor/admin têm `policies.inspections.read`; os demais perfis
  // veem, no máximo, a Carta de Melhoria (via endpoint de proposta).
  const { hasPermission: canReadInspections } = useRequirePermissions({
    permissions: ['policies.inspections.read'],
  });
  const isSubAdmin = canReadInspections === true;

  const { data: improvement } = useImprovementLetter(policyId);
  const hasImprovement = Boolean(
    improvement?.text || improvement?.attachments?.length
  );

  // Dados de inspeção só são buscados para sub/admin (evita 403 nos demais).
  const { data: policyInspections } = usePolicyInspections(policyId, {
    enabled: isSubAdmin,
  });
  const { data: inspectionHistory } = useInspectionHistory(policyId, {
    enabled: isSubAdmin,
  });

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isInteractionsModalOpen, setIsInteractionsModalOpen] = useState(false);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false);
  const [isImprovementModalOpen, setIsImprovementModalOpen] = useState(false);
  const [downloadingRefusal, setDownloadingRefusal] = useState(false);

  const latestInspectionId = useMemo(() => {
    if (!policyInspections?.length) return '';

    const sorted = [...policyInspections].sort((a, b) => {
      const seqDiff = (b.sequenceNumber ?? 0) - (a.sequenceNumber ?? 0);
      if (seqDiff !== 0) return seqDiff;
      const dateA = new Date(a.createdAt ?? a.createDate ?? 0).getTime();
      const dateB = new Date(b.createdAt ?? b.createDate ?? 0).getTime();
      return dateB - dateA;
    });

    return sorted[0]?.id ?? '';
  }, [policyInspections]);

  const { data: inspectionRecords } = useRecordsInspection(
    policyId,
    latestInspectionId,
    {
      enabled: isSubAdmin && Boolean(policyId && latestInspectionId),
    }
  );

  const technicalOpinion = useMemo(() => {
    if (!inspectionRecords?.length) return '';
    return (
      inspectionRecords.find(record => record.technicalOpinion?.trim())
        ?.technicalOpinion ?? ''
    );
  }, [inspectionRecords]);

  const isRejected = useMemo(
    () =>
      inspectionHistory?.some(record => record.action?.includes('Recusada')) ??
      false,
    [inspectionHistory]
  );

  async function handleDownloadLetter(
    type: 'refusal_letter' | 'improvement_letter',
    setLoading: (v: boolean) => void,
    fallbackName: string,
    notFoundDescription: string
  ) {
    setLoading(true);
    try {
      const response = await inspectionsService.getInspectionDocument(
        policyId,
        type
      );

      if (!response.success || !response.data?.url) {
        toaster.create({
          type: 'error',
          title: 'Documento não encontrado',
          description: notFoundDescription,
        });
        return;
      }

      const link = document.createElement('a');
      link.href = response.data.url;
      link.download = response.data.key.split('/').pop() || fallbackName;
      link.target = '_blank';
      link.click();
    } catch {
      toaster.create({
        type: 'error',
        title: 'Erro ao baixar documento',
        description: 'Não foi possível baixar o documento. Tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }

  const hasInspection = Boolean(policyId && latestInspectionId);

  const improvementButton = (
    <Button
      variant='secondary'
      size='sm'
      disabled={!hasImprovement}
      onClick={() => setIsImprovementModalOpen(true)}
      className='w-full justify-center sm:w-auto'
    >
      <MessageSquareText className='w-4 h-4 mr-2' />
      Carta de Melhoria
    </Button>
  );

  const improvementModal = (
    <ImprovementLetterModal
      isOpen={isImprovementModalOpen}
      onClose={() => setIsImprovementModalOpen(false)}
      text={improvement?.text}
      attachments={improvement?.attachments}
    />
  );

  // Sub/Admin: seção completa. O botão de Carta de Melhoria fica sempre visível
  // (desabilitado quando não há carta) e abre o modal com o texto.
  if (isSubAdmin) {
    return (
      <>
        <AccordionSection title='Inspeção'>
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-bold text-alba-black-500'>
                Parecer Técnico
              </span>
              <p className='text-sm text-alba-gray-600 whitespace-pre-line'>
                {technicalOpinion || '-'}
              </p>
            </div>

            <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3'>
              <Button
                variant='secondary'
                size='sm'
                disabled={!policyId}
                onClick={() => setIsHistoryModalOpen(true)}
                className='w-full justify-center sm:w-auto'
              >
                <History className='w-4 h-4 mr-2' />
                Histórico de Inspeções
              </Button>
              <Button
                variant='secondary'
                size='sm'
                disabled={!hasInspection}
                onClick={() => setIsInteractionsModalOpen(true)}
                className='w-full justify-center sm:w-auto'
              >
                <MessageSquareText className='w-4 h-4 mr-2' />
                Registro de Interações
              </Button>
              <Button
                variant='secondary'
                size='sm'
                disabled={!hasInspection}
                onClick={() => setIsAttachmentsModalOpen(true)}
                className='w-full justify-center sm:w-auto'
              >
                <Folder className='w-4 h-4 mr-2' />
                Arquivos da Inspeção
              </Button>
              {isRejected && (
                <Button
                  variant='secondary'
                  size='sm'
                  disabled={downloadingRefusal}
                  onClick={() =>
                    handleDownloadLetter(
                      'refusal_letter',
                      setDownloadingRefusal,
                      `carta-recusa-${policyId}.pdf`,
                      'A carta de recusa não está disponível para esta apólice.'
                    )
                  }
                >
                  <FileText className='w-4 h-4 mr-2' />
                  {downloadingRefusal ? 'Baixando...' : 'Carta de Recusa'}
                </Button>
              )}
              {improvementButton}
            </div>
          </div>
        </AccordionSection>

        <InspectionHistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          policyId={policyId}
          inspectionId={latestInspectionId}
        />

        <InspectionInteractionsModal
          isOpen={isInteractionsModalOpen}
          onClose={() => setIsInteractionsModalOpen(false)}
          policyId={policyId}
          inspectionId={latestInspectionId}
        />

        <InspectionAttachmentsModal
          isOpen={isAttachmentsModalOpen}
          onClose={() => setIsAttachmentsModalOpen(false)}
          policyId={policyId}
          inspectionId={latestInspectionId}
        />

        {improvementModal}
      </>
    );
  }

  // Não sub/admin com carta: container simplificado (só o botão da carta).
  if (canReadInspections === false && hasImprovement) {
    return (
      <>
        <AccordionSection title='Inspeção'>
          <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3'>
            {improvementButton}
          </div>
        </AccordionSection>

        {improvementModal}
      </>
    );
  }

  // Não sub/admin sem carta (ou permissão ainda carregando): não renderiza nada.
  return null;
}
